export default {
    ght_about_section_text: (
        <>
            <p>
                <b>About:</b> This Tool consolidates monitoring activity, estimated health loss and
                prospective threat level forecasts into one tool. IHME actively monitors diseases
                with endemic or pandemic potential and investigates emerging conditions, providing
                data to inform a proactive health threat detection system.
            </p>
            <br />
            <p>
                IHME tracks reported cases as inputs into estimations of true health loss and
                anticipates future threats. The rigorous modeling process converts reported cases
                into a wholistic measure of health burden, disability-adjusted life years (DALYS),
                which provide users to assess the health threat posed by a specific disease in each
                location. E.g. a small number of cases of Covid-19 may cause relatively little
                health loss due to access to care and high vaccination rates in a specific location;
                whereas the same number of cases of meningitis may cause greater threat in that same
                location.
            </p>
        </>
    ),
    ght_location_definitions_text: (
        <>
            <b>Location Definitions</b>
            <br />
            <ul>
                <li>
                    Bay Area, California, USA: Napa County, Sonoma County, Marin County, Solano
                    County, Contra Costa County, Alameda County, San Francisco County, San Mateo
                    County, Santa Clara County
                </li>
                <br />
                <li>Silicon Valley, California: San Mateo County, Santa Clara County</li>
            </ul>
        </>
    ),
    ght_qa_how_measure_loss_in_population_title:
        'How does IHME measure health loss in a population?',
    ght_qa_how_measure_loss_in_population_text_1:
        'IHME estimates the incidence, prevalence, and deaths of health conditions.  In addition, IHME considers holistic measures of health loss that allow comparisons across conditions that have very different outcomes. These measures are years lived with disability, years of life lost, and disability-adjusted live years or DALYs.',
    ght_qa_how_measure_loss_in_population_text_2: (
        <>
            <p>
                To effectively track current threats to population health, disability-adjusted life
                years have been used as the metric of interest. Using DALYs as the core metric
                allows both of the following scenarios to be flagged as a health threat:{' '}
            </p>
            <ul>
                <li>
                    emergence of a relatively rare but extremely fatal condition (e.g. ebola); and
                </li>
                <li>rising cases of a less fatal, more widespread condition (e.g. COVID-19).</li>
            </ul>
        </>
    ),
    ght_qa_how_categorize_loss_into_meaningful_threat_levels_title:
        'How does IHME categorize health loss into meaningful threat levels?',
    ght_qa_how_categorize_loss_into_meaningful_threat_levels_text:
        'Looking back on the past century, possible health loss within a population can range from 0 DALYs per week per 100,000 people (indicating a hypothetical perfectly healthy society) to 1,000,000 DALYs per week per 100,000 people (indicating a catastrophe on the scale of the Spanish flu in 1918-1919). Using a log scale, IHME divided this range into 8 DEFCON-style levels where Level 1 (that is, anywhere between 100K and 1M DALYs per 100K per week) indicates the highest level of alarm. Across time and location, COVID-19 would have most frequently fallen into a level 5 during 2020, although there were weeks and locations where it approached a level 2.',
    ght_qa_how_monitor_and_categorize_potential_health_threats_title:
        'How does IHME monitor and categorize potential health threats on an ongoing basis?',
    ght_qa_how_monitor_and_categorize_potential_health_threats_text: (
        <>
            <p>
                In each location, the threat level of each pathogen is determined for the recent
                past and forecast 4 weeks into the future.
            </p>
            <p>Taking dengue in Singapore as an example: </p>
            <ul>
                <li>
                    IHME monitors for the best available reported data of dengue cases and derives{' '}
                    <b>weekly reported cases</b> for Singapore.
                </li>
                <li>
                    Based on historic case estimates and historic DALY estimates for the past 30
                    years, IHME calculates the number of weekly reported dengue cases that would
                    result in one year of healthy life lost (a.k.a., one DALY) due to dengue in
                    Singapore. This is reported as <b>DALYs per weekly reported case.</b>
                </li>
                <li>
                    <b>DALYs per week</b> are calculated as = (Weekly reported cases) * (DALYs per
                    weekly reported case). This value, adjusted for Singapore’s population, places
                    dengue in Singapore into a threat level for any given week.
                </li>
            </ul>
        </>
    ),
    ght_qa_how_measure_loss_in_population_table_header_measure: 'Measure',
    ght_qa_how_measure_loss_in_population_table_header_definition: 'Definition',
    ght_measure_1_definition:
        'The death rate, or mortality rate, in GBD is the number of deaths due to a given cause, divided by the mid-year population.',
    ght_measure_2_definition:
        'Years of healthy life lost. Sum of YLLs and YLDs. An overall metric of the burden of disease. DALYs include infection rates, severe disease and death.',
    ght_measure_3_definition:
        'Years lived with disability for a cause in an age-sex group equals the prevalence of the condition times the disability weight for that condition.',
    ght_measure_4_definition:
        'Years of life lost due to premature mortality. YLLs are the multiplication of deaths and a standard life expectancy at the age of death.',
    ght_measure_5_definition:
        'The proportion of people in a population who present a case of disease, injury or sequela. All results in GBD refer to point prevalence.',
    ght_measure_6_definition:
        'In GBD, incidence rate is expressed as the number of new cases in a year divided by the mid-year population size.',
    ght_measure_21_definition:
        '0-to-1 weight that represents the severity of health loss associated with a specific health condition.  0 = full health. 1 = death.',
};
